import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import './EmployeeProfile.css';

import { API_BASE_URL } from '../../config';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';

import EmployeeIDCard from './components/EmployeeIDCard';
import PageHeader from '../../components/common/PageHeader';
import AssetsCard from '../../components/Employees/AssetsCard';
import EducationCard from '../../components/Employees/EducationCard';
import ExperienceCard from '../../components/Employees/ExperienceCard';
import PerformanceCard from '../../components/Employees/PerformanceCard';
import ClientProfileCard from '../../components/Clients/ClientProfileCard';
import PersonalInformations from '../../components/Employees/PersonalInformations';

import { notification } from '../../utils/notification';
import { timeStampFormatDate } from '../../utils/handler';
import { HEADER_TITLE } from '../../utils/constant/header';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { LB_EMPLOYEE_PROFILE } from '../../utils/constant/labels';
import { useDisconnectGoogleCalendarMutation } from '../../redux/apis/googleCalendarApi';
import { selectApiLocality } from '../../redux/utils/apiLocality';
import { useGetAssetFieldsQuery } from '../../redux/apis/AssetFieldsApi';
import { mergeAssetFields } from '../../utils/constant/defaultAssetFields';

const initialState = {
  isModal: false,
  modalData: '',
  loading: false,
  idCardDrawerOpen: false,
  employeeInformationDetails: [],
  employeeEducationDetails: [],
  employeeExperienceDetails: []
};

const {
  LB_TITLE_INFO,
  LB_TITLE_INFO_WORK,
  LB_DOB,
  LB_GENDER,
  LB_NATIONALITY,
  LB_MARITAL_STATUS,
  LB_EMERGENCY_CONTACT,
  LB_PERSONAL_EMAIL,
  LB_ADDRESS,
  LB_REPORTING_MANAGER,
  LB_SOURCE_OF_HIRE,
  LB_LOCATION,
  LB_DATE_OF_JOINING,
  LB_EMPLOYMENT_TYPE
} = LB_EMPLOYEE_PROFILE;

const { EMPLOYEE_PROFILE } = HEADER_TITLE;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const toPeople = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.filter((p) => p?.name);
};

const formatTenure = (joinedMs) => {
  const joined = Number(joinedMs);
  if (!Number.isFinite(joined) || joined <= 0) return '-';
  const totalMonths = moment().diff(moment(joined), 'months');
  if (totalMonths < 1) return 'New joiner';
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return [years ? `${years}y` : '', months ? `${months}m` : ''].filter(Boolean).join(' ');
};

const PeopleChips = ({ people }) =>
  people.length ? (
    <span className="ep-people">
      {people.map((p) => (
        <span key={p._id || p.name} className="ep-people__chip">
          <span className="ep-people__initial" aria-hidden>
            {p.name.slice(0, 1).toUpperCase()}
          </span>
          {p.name}
        </span>
      ))}
    </span>
  ) : (
    '-'
  );

function EmployeeProfile() {
  const { data, refetch } = useIsLoggedInQuery();
  const values = data?.data;
  const locality = useSelector(selectApiLocality);
  const isAuOffice = locality.includes('AU');

  const [disconnectGoogleCalendar, { isLoading }] = useDisconnectGoogleCalendarMutation();

  const handleDisconnectGoogleCalendar = async () => {
    try {
      const res = await disconnectGoogleCalendar();

      if (res.data.success) {
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        refetch();
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const [state, setState] = useState(initialState);

  const { data: assetFieldsData } = useGetAssetFieldsQuery({}, { skip: !locality?.length });

  const assetFields = useMemo(() => {
    const list = Array.isArray(assetFieldsData?.data)
      ? assetFieldsData.data
      : Array.isArray(assetFieldsData?.data?.data)
        ? assetFieldsData.data.data
        : [];
    return mergeAssetFields(
      list
        .filter((f) => typeof f?._id === 'string' && typeof f?.key === 'string')
        .map((f) => ({ id: f._id, key: f.key, label: f.name || f.key }))
    );
  }, [assetFieldsData]);

  const heroStats = useMemo(() => {
    if (!values) return [];
    return [
      {
        label: 'Joined',
        icon: 'bi-calendar-check',
        accent: 'blue',
        value: values.dateOfJoining ? timeStampFormatDate(values.dateOfJoining) : '-'
      },
      {
        label: 'Tenure',
        icon: 'bi-clock-history',
        accent: 'purple',
        value: formatTenure(values.dateOfJoining)
      },
      {
        label: 'Employment',
        icon: 'bi-briefcase',
        accent: 'amber',
        value: values.employmentType || '-'
      },
      {
        label: 'Work From Home',
        icon: 'bi-house',
        accent: values.allowWorkFromHome ? 'green' : 'slate',
        value: values.allowWorkFromHome ? 'Allowed' : 'Not allowed',
        tone: values.allowWorkFromHome ? 'yes' : 'no'
      }
    ];
  }, [values]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (values) {
      const personalInformation = [
        {
          title: LB_TITLE_INFO,
          icon: 'bi-person',
          information: [
            {
              title: LB_DOB,
              icon: 'bi-calendar',
              value: values.dateOfBirth ? timeStampFormatDate(values.dateOfBirth) : '-'
            },
            { title: LB_GENDER, icon: 'bi-person', value: values.gender || '-' },
            { title: LB_NATIONALITY, icon: 'bi-flag', value: values.nationality || '-' },
            { title: LB_MARITAL_STATUS, icon: 'bi-heart', value: values.maritalStatus || '-' },
            {
              title: LB_EMERGENCY_CONTACT,
              icon: 'bi-telephone',
              value: values.emergencyMobile || '-'
            },
            {
              title: LB_PERSONAL_EMAIL,
              icon: 'bi-envelope',
              value: values.personalEmail || '-',
              truncate: true,
              copyable: true
            },
            { title: LB_ADDRESS, icon: 'bi-house', value: values.location || '-', wide: true }
          ]
        },
        {
          title: LB_TITLE_INFO_WORK,
          icon: 'bi-briefcase',
          information: [
            {
              title: LB_REPORTING_MANAGER,
              icon: 'bi-people',
              value: <PeopleChips people={toPeople(values.reportingManager)} />,
              text: toPeople(values.reportingManager)
                .map((p) => p.name)
                .join(', ')
            },
            {
              title: 'Team Lead',
              icon: 'bi-person-badge',
              value: <PeopleChips people={toPeople(values.reportingTeamLead)} />,
              text: toPeople(values.reportingTeamLead)
                .map((p) => p.name)
                .join(', ')
            },
            {
              title: LB_DATE_OF_JOINING,
              icon: 'bi-calendar-check',
              value: values.dateOfJoining ? timeStampFormatDate(values.dateOfJoining) : '-'
            },
            {
              title: LB_EMPLOYMENT_TYPE,
              icon: 'bi-briefcase',
              value: values.employmentType || '-'
            },
            { title: LB_SOURCE_OF_HIRE, icon: 'bi-award', value: values.sourceOfHire || '-' },
            {
              title: 'Work From Home',
              icon: 'bi-house',
              value: (
                <span className={`ep-flag ep-flag--${values.allowWorkFromHome ? 'yes' : 'no'}`}>
                  {values.allowWorkFromHome ? 'Allowed' : 'Not allowed'}
                </span>
              ),
              text: values.allowWorkFromHome ? 'Allowed' : 'Not allowed'
            },
            {
              title: LB_LOCATION,
              icon: 'bi-geo-alt',
              value: values.companyAddress || '-',
              wide: true
            }
          ]
        }
      ];

      const educationDetails = values.education || [];
      const experiencerDetails = values.experience || [];

      setState({
        ...state,
        employeeExperienceDetails: experiencerDetails,
        employeeInformationDetails: personalInformation,
        employeeEducationDetails: educationDetails
      });
    }
  }, [values]);

  const handleEditEducation = (educationDetails) => {
    setState({ ...state, isModal: true, modalData: educationDetails });
  };

  const handleEditExperience = (experienceDetails) => {
    setState({ ...state, isModal: true, modalData: experienceDetails });
  };

  const handleEditInformation = (personalDetails) => {
    setState({ ...state, isModal: true, modalData: personalDetails });
  };

  const handleShow = () => {
    setState({ ...state, idCardDrawerOpen: true });
  };

  const downloadIdCard = async () => {
    try {
      if (!values?.zohoEmployeeId) {
        notification('Employee ID is required', TOAST_ERROR);
        return;
      }
      setState((prevState) => ({ ...prevState, loading: true }));

      const response = await fetch(
        `${API_BASE_URL}/users/download-employee-id-card/${values._id}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/pdf'
          }
        }
      );

      const blob = await response.blob();

      if (blob.type !== 'application/pdf') {
        notification('Invalid Blob received', TOAST_ERROR);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      const a = document.createElement('a');
      a.href = url;
      a.download = `ID_Card${values?.zohoEmployeeId ? `_${values.zohoEmployeeId}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setState((prevState) => ({ ...prevState, loading: false }));
      notification('ID Card download successfully', TOAST_SUCCESS);
    } catch (error) {
      setState((prevState) => ({ ...prevState, loading: false }));
      notification('Error downloading employee ID card', TOAST_ERROR);
    }
  };

  return (
    <div className="container-xxl employee-profile-page">
      <PageHeader
        headerTitle={EMPLOYEE_PROFILE}
        renderRight={() => {
          return (
            <div className="col-auto d-flex w-sm-100">
              <Button variant="primary" onClick={handleShow} className="d-flex align-items-center">
                <i className="icofont-eye me-2 fs-5"></i>
                ID Card
              </Button>
            </div>
          );
        }}
      />
      <div className="d-flex flex-column flex-md-row mb-4" style={{ height: '100%' }}>
        <div className="flex-fill me-md-3 d-flex flex-column">
          <div className="flex-shrink-0">
            <ClientProfileCard profileData={values} stats={heroStats} />
          </div>
          <div className="flex-grow-1 d-flex flex-column">
            <div className="row g-3 flex-fill">
              {state.employeeInformationDetails.map((d, i) => (
                <div key={'lkshnd' + i} className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                  <PersonalInformations
                    information={d.information}
                    title={d.title}
                    icon={d.icon}
                    onClickEdit={() => handleEditInformation(d)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {!isAuOffice ? (
          <div
            className="flex-shrink-0 d-flex ms-md-3 mt-3 mt-md-0 flex-column"
            style={{ minWidth: '260px' }}
          >
            <PerformanceCard
              data={state.employeeEducationDetails}
              onClickEdit={handleEditEducation}
            />
          </div>
        ) : null}
      </div>

      <div className="row g-3 mb-3 employee-profile-page__bottom">
        <div className="col-lg-4 col-md-6">
          <EducationCard
            data={state.employeeEducationDetails.filter((e) => e?.eduInfo?.institute)}
            onClickEdit={handleEditEducation}
          />
        </div>

        <div className="col-lg-4 col-md-6">
          <ExperienceCard
            data={state.employeeExperienceDetails}
            onClickEdit={handleEditExperience}
          />
        </div>

        <div className="col-lg-4 col-md-12">
          <AssetsCard
            className="h-100"
            assetFields={assetFields}
            assetsData={
              values?.assets && Object.keys(values?.assets)?.length > 0 ? values?.assets : ''
            }
          />
        </div>
      </div>

      {/* Google Calendar Disconnect Button  */}
      {data?.data?.isCalendarConnected && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            padding: '12px 16px',
            marginBottom: 20
          }}
          className="card"
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="avatar d-flex justify-content-center align-items-center rounded-circle light-success-bg">
              <i className="icofont-ui-calendar" style={{ color: '#212529', fontSize: 15 }} />
            </span>

            <div>
              <div style={{ fontWeight: 600, color: '#373a3c' }}>Google Calendar</div>
              <div
                style={{
                  fontSize: 12,
                  color: '#5f6368'
                }}
              >
                Connected
              </div>
            </div>
          </div>

          <button
            onClick={handleDisconnectGoogleCalendar}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '1px solid #f1bcbc',
              background: '#fff',
              color: '#d93025',
              width: '109px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {isLoading ? <Spinner size="sm" /> : 'Disconnect'}
          </button>
        </div>
      )}

      {/* <div className="row g-3">
        <div className="col-xl-8 col-lg-12 col-md-12 mb-3">
          <ClientProfileCard profileData={values} />
          <div className="row g-3 h-fit">
            {state.employeeInformationDetails.map((d, i) => {
              return (
                <div key={'lkshnd' + i} className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                  <PersonalInformations
                    information={d.information}
                    title={d.title}
                    onClickEdit={() => handleEditInformation(d)}
                  />
                </div>
              );
            })}
          </div>
          <div className="row g-3 h-fit mt-1">
            {state.employeeEducationDetails.length > 0 &&
              state.employeeEducationDetails[0].eduInfo.institute && (
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                  <EducationCard
                    data={state.employeeEducationDetails}
                    onClickEdit={handleEditEducation}
                  />
                </div>
              )}

            {state.employeeExperienceDetails.length > 0 && (
              <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                <ExperienceCard
                  data={state.employeeExperienceDetails}
                  onClickEdit={handleEditExperience}
                />
              </div>
            )}
          </div>
        </div>
        <div className="col-xl-4 col-lg-12 col-md-12">
          <div className="mb-4">
            <PerformanceCard
              data={state.employeeEducationDetails}
              onClickEdit={handleEditEducation}
            />
          </div>

          <div>
            <AssetsCard
              assetsData={
                values?.assets && Object.keys(values?.assets)?.length > 0 ? values?.assets : ''
              }
            />
          </div>
        </div>
      </div> */}
      <Modal
        centered
        show={state.isModal}
        onHide={() => {
          setState({ ...state, isModal: false });
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">{state.modalData.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="deadline-form">
            <form>
              <div className="row g-3 mb-3">
                {state.modalData
                  ? state.modalData.information.map((d, i) => {
                      if (i < 2) {
                        return (
                          <div key={'kjsdfhj' + i} className="col">
                            <label className="form-label">{d.title}</label>
                            <input
                              type="text"
                              className="form-control"
                              id="exampleFormControlInput877"
                              value={d.text ?? d.value}
                            />
                          </div>
                        );
                      }
                      return null;
                    })
                  : null}
              </div>
              <div className="row g-3 mb-3">
                {state.modalData
                  ? state.modalData.information.map((d, i) => {
                      if (i > 1 && i < 4) {
                        return (
                          <div key={'kjsdfhj' + i} className="col">
                            <label className="form-label">{d.title}</label>
                            <input
                              type="text"
                              className="form-control"
                              id="exampleFormControlInput877"
                              value={d.text ?? d.value}
                            />
                          </div>
                        );
                      }
                      return null;
                    })
                  : null}
              </div>
              <div className="row g-3 mb-3">
                {state.modalData
                  ? state.modalData.information.map((d, i) => {
                      if (i > 3) {
                        return (
                          <div key={'kjsdfhj' + i} className="col">
                            <label className="form-label">{d.title}</label>
                            <input
                              type="text"
                              className="form-control"
                              id="exampleFormControlInput877"
                              value={d.text ?? d.value}
                            />
                          </div>
                        );
                      }
                      return null;
                    })
                  : null}
              </div>
            </form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setIsmodal(false);
            }}
          >
            Done
          </button>
          <button type="button" className="btn btn-primary">
            Sent
          </button>
        </Modal.Footer>
      </Modal>

      {/* Employee ID Card Modal */}
      <Modal
        centered
        className="id-card-modal-width"
        show={state.idCardDrawerOpen}
        onHide={() => {
          setState({ ...state, idCardDrawerOpen: false });
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {' '}
            <div className="card-header d-flex gap-2">
              <h6 className="mb-0 fw-bold text-uppercase text-primary">ID Card</h6>

              <span
                className="fs-6 cursor-pointer"
                style={{ color: '#1E3A8A', borderColor: '#1E3A8A' }}
                onClick={downloadIdCard}
              >
                {state?.loading ? (
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                ) : (
                  <i
                    className="icofont-download"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Download"
                  ></i>
                )}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-3 flex-column flex-md-row">
            <div className="flex-fill d-flex flex-column align-items-center">
              <EmployeeIDCard
                employee={{
                  photo: values?.profilePicture,
                  name: values?.name || '',
                  id: values?.zohoEmployeeId || '',
                  designation: values?.designation?.name,
                  department: values?.department || '',
                  email: values?.email,
                  company: 'Software Co',
                  dateOfBirth: values?.dateOfBirth || '',
                  emergencyMobile: values?.emergencyMobile || '',
                  phone: values?.phone,
                  employeeMongoId: values?._id
                }}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default EmployeeProfile;
